import { Link } from "@fluentui/react-components";
import { GridColDef, GridRenderCellParams, GridTreeNodeWithRender } from "@mui/x-data-grid";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { logEvent } from "../../../helper/AppInsightsHelper";
import {
  ALLREPORTS_DATAGRID_STYLE,
  INITIAL_GRID_FILTER_MODEL,
  REPORT_CATALOG_COLUMNS,
  REPORT_LINK_STYLE
} from "../ReportCatalog.common";
import { CustomDataGrid } from "../../../SharedComponents/CustomDataGrid/CustomDataGrid";
import { DATA_GRID_CONFIG } from "../../../SharedComponents/Constants/CustomCustomerList.constants";

export default function AllReportsDataGrid(props: any) {
  const [reportCatalogData, setReportCatalogData] = React.useState(props.rowData);
  const [filterModel, setFilterModel] = React.useState(INITIAL_GRID_FILTER_MODEL);
  const [paginationOptions, setPaginationOptions] = React.useState(DATA_GRID_CONFIG.pagination);
  const navigate = useNavigate();
  const columns: GridColDef[] = [
    {
      ...REPORT_CATALOG_COLUMNS.slice(0, 1)[0],
      renderCell: (params) => (
        <Link onClick={() => handleReportLinkClick(params)} style={REPORT_LINK_STYLE} title={params.row.report}>
          {params.row.report}
        </Link>
      )
    },
    ...REPORT_CATALOG_COLUMNS.slice(1)
  ];

  const handleReportLinkClick = React.useCallback(
    (params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>) => {
      const reportLink = params.row.reportLink;
      const reportParser = reportLink?.split("/reports")[1];
      const reportId = reportParser?.split("/")[1] ?? ("" as string);
      const customProps = {
        reportId: params.id,
        reportLink: reportLink
      };
      logEvent("Report Link Clicked", "Report Catalog", "AllReportsDataGrid", customProps);
      // Only navigate internally if 'powerbi.com' is present and 'apps' is not in the link; otherwise, open in a new window
      const isPowerBI = reportLink && reportLink.includes("powerbi.com");
      const containsApps = reportLink && reportLink.includes("apps");
      if (isPowerBI && !containsApps) {
        navigate(`/reportview/reportid=${reportId}`, {
          state: { reportName: params.row.report, reportLink: reportLink }
        });
      } else if (reportLink) {
        window.open(reportLink, "_blank");
      }
    },
    []
  );

  const handleGridRowsAndFilterModel = React.useCallback(
    (data: any[]) => {
      setReportCatalogData(data);
      setFilterModel((prev) => ({
        ...prev,
        quickFilterValues: [props.searchReportVal]
      }));
    },
    [props.searchReportVal]
  );

  React.useEffect(() => {
    /**
     * @description
     * Filters the grid rows based on the selected products (`props.selProds`) by checking if the `product` field
     * of each row in `props.rowData` includes any selected product value (case-insensitive). Ensures uniqueness by using a `Set`
     * to avoid duplicate rows. The filtered rows are then passed to `handleGridRowsAndFilterModel` to update the grid's data
     * and filter model.
     */
    const gridRows: any[] = [];
    const uniqueRows = new Set();

    props.selProds?.forEach((filterele: any) => {
      props.rowData?.forEach((element: any) => {
        const isProductIncludes = element.product.toLowerCase().includes(filterele.toLowerCase());

        if (isProductIncludes && !uniqueRows.has(element.id)) {
          uniqueRows.add(element.id);
          gridRows.push(element);
        }
      });
    });
    handleGridRowsAndFilterModel(gridRows);
  }, [handleGridRowsAndFilterModel, props]);

  return (
    <div style={{ minHeight: 200 }}>
      <CustomDataGrid
        columns={columns}
        filterModel={filterModel}
        sx={ALLREPORTS_DATAGRID_STYLE}
        rows={{ values: reportCatalogData ?? [], count: reportCatalogData?.length ?? 0 }}
        getRowId={(row) => row.id}
        showExploreDataButtonOnFooter={false}
        paginationOptions={paginationOptions}
        setPaginationOptions={setPaginationOptions}
        initialState={{
          pagination: {
            paginationModel: { page: 0, pageSize: 25 }
          }
        }}
        toolbarConfig={{ disableGridToolbarQuickFilter: true }}
      />
    </div>
  );
}
